import { memo, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Barcode from 'react-barcode';
import { BodyCopy } from './BodyCopy';
import { Button, GradientText } from 'lola-framework-ui-test';
import { useStore } from '../store/globalState';
import { CASH_DEPOSIT_ROUTES, TITLE_COLOR } from '../constants';
import { formatHeroAmount } from '../utils';
import vanillaIcon from '../assets/vanillaIcon.png';

/** Shared strip height (px). Matches `vanillaIcon.png` intrinsic height (79). */
const BARCODE_STRIP_HEIGHT = 79;

/** POS scanners need high-contrast bars; never bind this to the partner theme. */
const BARCODE_LINE_COLOR = '#000000';

export interface BarcodeDrawerSlotProps {
  amount: number;
  currency: string;
  barcodeValue: string;
  expMinutes: number;
  expiresAt?: string;
  onClose: () => void;
  /** Gallery-only: double-click the amount to preview the mismatch top drawer. */
  onAmountDoubleClick?: () => void;
}

/** Formats a remaining-seconds value as mm:ss. */
const formatCountdown = (totalSeconds: number): string => {
  const safe = Math.max(0, totalSeconds);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

/**
 * Gallery-safe barcode drawer. Same anatomy as the cash-deposit widget:
 * tender amount at countdown scale, live mm:ss in the expiry hint, Vanilla
 * mark + CODE128. Funding confirmation is automatic. While the barcode is
 * valid, "I already paid" closes this drawer the same way as the close icon.
 * Expired barcodes show a regenerate CTA.
 */
export const BarcodeDrawerSlot = memo(
  ({
      amount,
      currency,
      barcodeValue,
    expMinutes,
    expiresAt,
    onClose,
    onAmountDoubleClick,
  }: BarcodeDrawerSlotProps) => {
    const { t } = useTranslation();
    const { theme } = useStore();
    const colorConfig = theme.whiteView;
    const { backgroundBtn, textColorBtn, bodyCopy, footerColor, title } =
      colorConfig;
    const navigate = useNavigate();

    const payAmount = formatHeroAmount(amount, currency);

    const [deadline] = useState(() => {
      if (expiresAt) {
        const parsed = new Date(expiresAt).getTime();
        if (Number.isFinite(parsed)) return parsed;
      }
      return Date.now() + expMinutes * 60_000;
    });

    const [remaining, setRemaining] = useState(() =>
      Math.round((deadline - Date.now()) / 1000),
    );

    useEffect(() => {
      const id = setInterval(() => {
        const next = Math.round((deadline - Date.now()) / 1000);
        setRemaining(next);
        if (next <= 0) clearInterval(id);
      }, 1000);
      return () => clearInterval(id);
    }, [deadline]);

    const expired = remaining <= 0;

    const themeVars = useMemo(
      () =>
        ({
          '--barcode-accent': backgroundBtn,
          '--barcode-card-border': bodyCopy,
          '--barcode-card-surface': colorConfig.background,
          '--barcode-strip-height': `${BARCODE_STRIP_HEIGHT}px`,
        }) as React.CSSProperties,
      [backgroundBtn, bodyCopy, colorConfig.background],
    );

    return (
      <section className="barcode-drawer-slot" style={themeVars}>
        <GradientText
          as="h2"
          textColor={bodyCopy}
          className="barcode-drawer-title"
        >
          {t('barcode.title')}
        </GradientText>

        <GradientText
          as="p"
          textColor={bodyCopy}
          className="barcode-pay-amount"
          aria-label={`${t('barcode.totalPayment')} ${payAmount.prefix}${payAmount.intPart}.${payAmount.decPart}${payAmount.suffix}`}
          onDoubleClick={onAmountDoubleClick}
        >
          <span className="barcode-pay-amount__value">
            {payAmount.prefix}
            {payAmount.intPart}.{payAmount.decPart}
          </span>
          <span className="barcode-pay-amount__currency">
            {payAmount.suffix}
          </span>
        </GradientText>

        <div className="barcode-strip-row">
          <div className="barcode-strip-group">
            <img
              src={vanillaIcon}
              className="barcode-vanilla-icon"
              alt={t('barcode.vanillaAlt')}
            />
            <div
              className={`barcode-strip-slot${expired ? ' is-expired' : ''}`}
            >
              <Barcode
                value={barcodeValue}
                format="CODE128"
                displayValue={false}
                renderer="svg"
                background="transparent"
                lineColor={BARCODE_LINE_COLOR}
                width={2}
                height={BARCODE_STRIP_HEIGHT}
                margin={0}
                className="barcode-strip"
              />
              {expired && (
                <div className="barcode-strip-placeholder" role="status">
                  <BodyCopy
                    textColor={TITLE_COLOR}
                    className="barcode-strip-placeholder__label"
                  >
                    {t('barcode.expiredStripLabel')}
                  </BodyCopy>
                </div>
              )}
            </div>
          </div>
        </div>

        <GradientText
          as="p"
          textColor={bodyCopy}
          className="barcode-expiry-hint"
        >
          {expired
            ? t('barcode.expired')
            : t('barcode.expiryHint', { time: formatCountdown(remaining) })}
        </GradientText>

        <section className="barcode-drawer-footer">
          <BodyCopy textColor={footerColor} className="barcode-disclaimer">
            {t('barcode.disclaimerLine1')} {t('barcode.disclaimerLine2')}{' '}
            <GradientText
              as="span"
              className="barcode-terms-link"
              textColor={title}
              onClick={() => navigate(CASH_DEPOSIT_ROUTES.LEGAL)}
            >
              {t('barcode.termsLink')}
            </GradientText>
            .
          </BodyCopy>

          <Button
            size="large"
            background={backgroundBtn}
            color={textColorBtn}
            onClick={onClose}
          >
            {expired ? t('barcode.regenerate') : t('barcode.alreadyPaid')}
          </Button>
        </section>
      </section>
    );
  },
);
